import { Injectable } from '@angular/core';

export interface TokenRegistrationState {
  incomplete_registration: boolean;
  required_steps: string[];
  registration_started_at: number;
  max_incomplete_duration: number; // in milliseconds
}

@Injectable({
  providedIn: 'root',
})
export class MzicTokenLocalService {
  private readonly TOKEN_STATE_KEY = 'mzic-token-registration-state';

  public clearAlltokens(): void {
    this.removeTokenLocal();
    this.removeRefreshToken();
    this.removeTokenRegistrationState();
  }
  public getTokenLocal(): string {
    return localStorage.getItem('mzic-token-local') as string;
  }

  public getRefreshToken(): string {
    return localStorage.getItem('mzic-refresh-token') as string;
  }

  public removeTokenLocal(): void {
    localStorage.removeItem('mzic-token-local');
  }

  public removeRefreshToken(): void {
    localStorage.removeItem('mzic-refresh-token');
  }

  public setTokenLocal(value: string): void {
    localStorage.setItem('mzic-token-local', value);
  }

  public setRefreshToken(value: string): void {
    localStorage.setItem('mzic-refresh-token', value);
  }

  /**
   * Sets tokens with incomplete registration state for two-phase authentication
   */
  public setTokenWithIncompleteRegistration(
    token: string,
    refreshToken: string,
    requiredSteps: string[] = ['terms_acceptance']
  ): void {
    // Set tokens
    this.setTokenLocal(token);
    this.setRefreshToken(refreshToken);

    // Set registration state
    const registrationState: TokenRegistrationState = {
      incomplete_registration: true,
      required_steps: requiredSteps,
      registration_started_at: Date.now(),
      max_incomplete_duration: 1800000, // 30 minutes
    };

    localStorage.setItem(this.TOKEN_STATE_KEY, JSON.stringify(registrationState));
    console.log('[Token Service] Set tokens with incomplete registration state');
  }

  /**
   * Completes the registration by removing the incomplete registration flag
   */
  public completeRegistration(): void {
    this.removeTokenRegistrationState();
    console.log('[Token Service] Registration completed, removed incomplete flag');
  }

  /**
   * Gets the current token registration state
   */
  public getTokenRegistrationState(): TokenRegistrationState | null {
    try {
      const stateJson = localStorage.getItem(this.TOKEN_STATE_KEY);
      if (!stateJson) return null;
      return JSON.parse(stateJson);
    } catch {
      this.removeTokenRegistrationState();
      return null;
    }
  }

  /**
   * Checks if current registration is incomplete and expired
   */
  public isIncompleteRegistrationExpired(): boolean {
    const state = this.getTokenRegistrationState();
    if (!state || !state.incomplete_registration) return false;

    const elapsed = Date.now() - state.registration_started_at;
    return elapsed > state.max_incomplete_duration;
  }

  /**
   * Checks if user has incomplete registration
   */
  public hasIncompleteRegistration(): boolean {
    const state = this.getTokenRegistrationState();
    return state?.incomplete_registration === true;
  }

  /**
   * Removes token registration state
   */
  private removeTokenRegistrationState(): void {
    localStorage.removeItem(this.TOKEN_STATE_KEY);
  }
}
